import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { renderMath } from "@/utils/mathjax";
import "survey-core/survey-core.min.css";
import "./ExitTicketSurvey.css";
import "@/features/tutor/utils/surveyCustomProperties";
import {
  choiceImage,
  choiceLabel,
  paintChoice,
} from "@/features/tutor/utils/surveyChoiceImage";
import { useAssessmentStore } from "../store/useAssessmentStore";
import { computePercentAnswered } from "../utils/lessonCompletion";
import ASSESSMENT_TYPES from "../constants/assessmentTypes";
import { useSlideStore } from "../store/useSlideStore";
import {
  LatexKeyboard,
  ImageLightbox,
  makeImagesZoomable,
} from "@ever-tutor/et-ui";
import NotificationStack from "@/components/notifications/NotificationStack";
import { Loader2 } from "lucide-react";
import { useViewport } from "@/hooks/useViewport";
import { markNoTranslate, noTranslateProps } from "@/utils/pageTranslation";
import {
  showDynamicTutorViewNotification,
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import {
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";

import { parseLatexWithCursor, parseLatex } from "@ever-tutor/et-ui";
import {
  serializeToLatex,
  serializeToDisplay,
  serializeWithSelection,
  serializeWithCursor,
  injectCursorStyles,
} from "@ever-tutor/et-ui";
import { normalizeSelection } from "@ever-tutor/et-ui";
import { mapKeyToInsertion } from "@ever-tutor/et-ui";
import {
  applyInsertion,
  backspace,
  moveLeft,
  moveRight,
  moveUp,
  moveDown,
  moveLeftClamped,
  moveRightClamped,
  tabForward,
  deleteRange,
  getSelectedNodes,
  selectAll,
  insertNodes,
} from "@ever-tutor/et-ui";
import {
  renderKatexWordWrapped,
  positionCursorOverlay,
  hitTestDeep,
  visualMoveUpDown,
} from "@ever-tutor/et-ui";

const AUTO_SAVE_DEBOUNCE_MS = 2000;
const MAX_UNDO = 20;
const SUBMIT_FAILED_MESSAGE =
  "We couldn't submit your answers. Please try again.";

// Passed as a render callback — ensures displayFractions is always enabled
function renderMathDisplay(el, latex) {
  renderMath(el, latex, { displayFractions: true });
}

function renderKatexDisplay(el, latex) {
  const doc = parseLatex(latex);
  const displayLatex = serializeToDisplay(doc);
  renderMathDisplay(el, displayLatex);
}

function renderKatexWrapped(el, latex) {
  const doc = parseLatex(latex);

  // Split top-level nodes at space text nodes
  const segments = [];
  let current = [];

  for (const node of doc.nodes) {
    if (node.type === "text" && node.value === " ") {
      segments.push(current);
      current = [];
    } else {
      current.push(node);
    }
  }
  segments.push(current);

  // No spaces found — render as single block
  if (segments.length <= 1) {
    renderKatexDisplay(el, latex);
    return;
  }

  el.innerHTML = "";
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].length > 0) {
      const wordSpan = document.createElement("span");
      wordSpan.className = "et-word-group";
      const segLatex = serializeToDisplay({ nodes: segments[i] });
      renderMathDisplay(wordSpan, segLatex);
      el.appendChild(wordSpan);
    }
    if (i < segments.length - 1) {
      const spaceSpan = document.createElement("span");
      spaceSpan.className = "et-editing-space";
      spaceSpan.textContent = " ";
      el.appendChild(spaceSpan);
    }
  }
}

function deleteSelectionIfPresent(doc, cursor, selectionAnchor) {
  if (!selectionAnchor) return null;
  const range = normalizeSelection(selectionAnchor, cursor);
  if (!range) return null;
  return deleteRange(doc, range);
}

function createSpeakerIcon() {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("viewBox", "0 0 22 22");
  svg.setAttribute("fill", "none");

  const paths = [
    "M17.7617 18.8476C17.4827 18.8476 17.2037 18.7402 16.992 18.5274C16.5686 18.1018 16.5708 17.4146 16.9963 16.9912C18.6378 15.3605 19.5422 13.1827 19.5422 10.8573C19.5422 8.53182 18.6378 6.35401 16.9963 4.72337C16.5708 4.29997 16.5686 3.61384 16.992 3.18718C17.4143 2.76161 18.1004 2.75944 18.5271 3.18284C20.5822 5.22494 21.7135 7.94992 21.7135 10.8573C21.7135 13.7646 20.5822 16.4896 18.5271 18.5317C18.3154 18.7423 18.0385 18.8476 17.7617 18.8476Z",
    "M14.6895 15.7746C14.4094 15.7746 14.1293 15.6671 13.9176 15.4522C13.4952 15.0255 13.4996 14.3383 13.9262 13.917C14.7351 13.1169 15.1997 12.0009 15.1997 10.8566C15.1997 9.71234 14.7351 8.59629 13.9262 7.79617C13.4996 7.37494 13.4963 6.68772 13.9176 6.26106C14.3388 5.83549 15.026 5.83114 15.4527 6.25238C16.6718 7.45744 17.371 9.13586 17.371 10.8566C17.371 12.5774 16.6718 14.2558 15.4527 15.4608C15.241 15.6704 14.9652 15.7746 14.6895 15.7746Z",
    "M10.8565 21.7134C10.5742 21.7134 10.2963 21.6027 10.0889 21.3953L4.97878 16.2852H2.1713C0.973826 16.2852 0 15.3113 0 14.1139V7.59998C0 6.40251 0.973826 5.42868 2.1713 5.42868H4.97878L10.0889 0.318539C10.3994 0.00695836 10.8662 -0.0853217 11.2723 0.0829537C11.6783 0.251229 11.9421 0.647491 11.9421 1.08609V20.6277C11.9421 21.0664 11.6783 21.4626 11.2723 21.6309C11.1377 21.6863 10.9965 21.7134 10.8565 21.7134Z",
  ];
  for (const d of paths) {
    const p = document.createElementNS(NS, "path");
    p.setAttribute("d", d);
    p.setAttribute("fill", "currentColor");
    svg.appendChild(p);
  }

  return svg;
}

const AssessmentSurvey = ({
  type = "exit_ticket",
  onComplete,
  onSaveAndExit,
}) => {
  const config = ASSESSMENT_TYPES[type];
  const { isMobile, isMobileLandscape } = useViewport();
  const slice = useAssessmentStore((s) => s.assessments[type]);
  const { surveyData, ticketId, attemptId, savedResponses } = {
    surveyData: slice.data,
    ticketId: slice.id,
    attemptId: slice.attemptId,
    savedResponses: slice.savedResponses,
  };

  const [surveyModel, setSurveyModel] = useState(null);
  const [submitState, setSubmitState] = useState("idle");
  const [lightboxImage, setLightboxImage] = useState(null);
  const audioRef = useRef(null);

  const [showMathKeyboard, setShowMathKeyboard] = useState(false);
  const [mathInputDisplayValue, setMathInputDisplayValue] = useState("");
  const [mathInputDisplayCursor, setMathInputDisplayCursor] = useState(null);
  const mathInputPositionRef = useRef(null);
  const activeMathQuestionRef = useRef(null);
  const mathInputValueRef = useRef("");
  const surveyModelRef = useRef(null);

  const autoSaveTimerRef = useRef(null);
  const submitStateRef = useRef(submitState);
  submitStateRef.current = submitState;

  const astDocRef = useRef(null);
  const astCursorRef = useRef(null);
  const selectionAnchorRef = useRef(null);

  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  const currentLessonName = useSlideStore((s) => s.currentLessonName);
  const autoSave = useCallback(
    (tid, data) => useAssessmentStore.getState().autoSave(type, data),
    [type],
  );
  const submitAttempt = useCallback(
    (tid, data) => useAssessmentStore.getState().submitAttempt(type, data),
    [type],
  );

  const debouncedAutoSave = useCallback(
    (data) => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => {
        autoSaveTimerRef.current = null;
        if (ticketId && Object.keys(data).length > 0) autoSave(ticketId, data);
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [ticketId, autoSave],
  );

  const flushAutoSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (!surveyModelRef.current || !ticketId) return;
    const data = surveyModelRef.current.data;
    if (Object.keys(data).length > 0) await autoSave(ticketId, data);
  }, [ticketId, autoSave]);

  useEffect(() => {
    injectCursorStyles();
  }, []);

  useEffect(() => {
    surveyModelRef.current = surveyModel;
  }, [surveyModel]);

  const renderAstDisplay = useCallback((questionName) => {
    const el = document.querySelector(
      `.et-katex-display[data-question-name="${questionName}"]`,
    );
    if (!el) return;
    const doc = astDocRef.current;
    const cursor = astCursorRef.current;
    if (!doc || !cursor) return;
    const anchor = selectionAnchorRef.current;
    const selection = anchor ? normalizeSelection(anchor, cursor) : null;
    const latex = serializeWithSelection(doc, cursor, selection);
    el.classList.add("et-katex-editing");
    renderKatexWordWrapped(el, latex, renderMathDisplay);
    el.style.color = "";
    positionCursorOverlay(el, cursor);
    setMathInputDisplayValue(serializeWithCursor(doc, cursor, true, true));
    setMathInputDisplayCursor(cursor);
  }, []);

  const pushUndo = useCallback(() => {
    const doc = astDocRef.current;
    const cursor = astCursorRef.current;
    if (!doc || !cursor) return;
    undoStackRef.current = [
      ...undoStackRef.current.slice(-(MAX_UNDO - 1)),
      { doc: JSON.parse(JSON.stringify(doc)), cursor: { ...cursor } },
    ];
    redoStackRef.current = [];
  }, []);

  const performUndo = useCallback(
    (questionName) => {
      if (undoStackRef.current.length === 0) return;
      const entry = undoStackRef.current[undoStackRef.current.length - 1];
      undoStackRef.current = undoStackRef.current.slice(0, -1);
      redoStackRef.current = [
        ...redoStackRef.current,
        {
          doc: JSON.parse(JSON.stringify(astDocRef.current)),
          cursor: { ...astCursorRef.current },
        },
      ];
      astDocRef.current = entry.doc;
      astCursorRef.current = entry.cursor;
      selectionAnchorRef.current = null;
      mathInputValueRef.current = serializeToLatex(entry.doc);
      renderAstDisplay(questionName);
    },
    [renderAstDisplay],
  );

  const performRedo = useCallback(
    (questionName) => {
      if (redoStackRef.current.length === 0) return;
      const entry = redoStackRef.current[redoStackRef.current.length - 1];
      redoStackRef.current = redoStackRef.current.slice(0, -1);
      undoStackRef.current = [
        ...undoStackRef.current,
        {
          doc: JSON.parse(JSON.stringify(astDocRef.current)),
          cursor: { ...astCursorRef.current },
        },
      ];
      astDocRef.current = entry.doc;
      astCursorRef.current = entry.cursor;
      selectionAnchorRef.current = null;
      mathInputValueRef.current = serializeToLatex(entry.doc);
      renderAstDisplay(questionName);
    },
    [renderAstDisplay],
  );

  useEffect(() => {
    if (!surveyData) return;

    const model = new Model(surveyData);
    model.showNavigationButtons = false;
    model.showCompletedPage = false;
    model.showProgressBar = "off";

    if (savedResponses) {
      model.data = savedResponses;
    }

    model.onAfterRenderQuestion.add((_, options) => {
      const question = options.question;
      const htmlElement = options.htmlElement;

      if (htmlElement.dataset.previewLatex === "true") return;
      htmlElement.dataset.previewLatex = "true";

      // Question images + answer-option graphics become click-to-enlarge.
      makeImagesZoomable(htmlElement, (src, alt) =>
        setLightboxImage({ src, alt }),
      );

      // ── Title overlay — outside .sd-question__title so SurveyJS span CSS doesn't leak ──
      const sdQuestionTitle = htmlElement.querySelector(".sd-question__title");
      if (sdQuestionTitle) {
        const titleOverlay = document.createElement("div");
        titleOverlay.className = "et-katex-display et-katex-display--title";
        sdQuestionTitle.style.display = "none";
        sdQuestionTitle.parentNode?.insertBefore(titleOverlay, sdQuestionTitle);

        const latexTitle = question.question;
        if (latexTitle) {
          renderKatexWrapped(titleOverlay, latexTitle);
        } else {
          titleOverlay.textContent = question.title || question.name || "";
        }

        if (question.generatedAudio) {
          const audioBtn = document.createElement("button");
          audioBtn.type = "button";
          audioBtn.className = "et-preview-audio-btn";
          audioBtn.title = "Play question audio";
          audioBtn.appendChild(createSpeakerIcon());

          let currentAudio = null;
          audioBtn.onclick = (e) => {
            e.stopPropagation();
            if (currentAudio && !currentAudio.paused) {
              currentAudio.pause();
              currentAudio.currentTime = 0;
              audioBtn.classList.remove("et-preview-audio-btn--playing");
              currentAudio = null;
              return;
            }
            currentAudio = new Audio(question.generatedAudio);
            audioBtn.classList.add("et-preview-audio-btn--playing");
            currentAudio.play();
            currentAudio.onended = () => {
              audioBtn.classList.remove("et-preview-audio-btn--playing");
              currentAudio = null;
            };
          };

          titleOverlay.appendChild(audioBtn);
        }
      }

      const questionType = question.getType();

      // ── Radio/checkbox labels — overlay outside .sd-item__control-label ──
      if (questionType === "radiogroup" || questionType === "checkbox") {
        const choices = question.visibleChoices || question.choices || [];
        const labelEls = htmlElement.querySelectorAll(
          ".sd-item__control-label",
        );
        let painted = false;
        labelEls.forEach((labelEl, idx) => {
          const choice = choices[idx];
          if (!choice) return;
          const image = choiceImage(choice, idx);
          const text = choiceLabel(choice);
          if (!text && !image) return;
          const overlay = document.createElement("div");
          overlay.className = "et-katex-display et-katex-display--radio-option";
          labelEl.style.display = "none";
          labelEl.parentNode?.insertBefore(overlay, labelEl);
          paintChoice(overlay, image, String(text), renderKatexWrapped);
          painted = painted || Boolean(image);
        });
        if (painted) {
          makeImagesZoomable(htmlElement, (src, alt) =>
            setLightboxImage({ src, alt }),
          );
        }
      }

      if (questionType === "text") {
        const inputEl = htmlElement.querySelector(".sd-input");
        if (!inputEl) return;

        const wrapper = document.createElement("div");
        // The .sd-input is hidden and this wrapper renders the student's own
        // answer as page text.
        wrapper.className = "et-preview-input-wrapper";
        markNoTranslate(wrapper);

        const katexDisplay = document.createElement("div");
        katexDisplay.className =
          "et-katex-display et-katex-display--preview-input";
        katexDisplay.dataset.questionName = question.name;
        katexDisplay.tabIndex = 0;

        const isActiveQuestion =
          activeMathQuestionRef.current === question.name;
        if (isActiveQuestion && astDocRef.current && astCursorRef.current) {
          const anchor = selectionAnchorRef.current;
          const selection = anchor
            ? normalizeSelection(anchor, astCursorRef.current)
            : null;
          const latex = serializeWithSelection(
            astDocRef.current,
            astCursorRef.current,
            selection,
          );
          renderKatexWordWrapped(katexDisplay, latex, renderMathDisplay);
          positionCursorOverlay(katexDisplay, astCursorRef.current);
        } else {
          const currentVal = String(question.value || "");
          if (currentVal) {
            renderKatexWrapped(katexDisplay, currentVal);
          } else {
            katexDisplay.textContent = "Type your answer here";
            katexDisplay.style.color = "#9ca3af";
          }
        }

        const isActive = () => activeMathQuestionRef.current === question.name;

        const startEditing = () => {
          if (isActive()) return;

          // Save any in-progress answer before switching to a new question.
          handleMathKeyboardClose();

          const existingValue = String(question.value || "");
          mathInputValueRef.current = existingValue;
          const { doc, cursor } = parseLatexWithCursor(existingValue);
          astDocRef.current = doc;
          astCursorRef.current = cursor;
          selectionAnchorRef.current = null;
          undoStackRef.current = [];
          redoStackRef.current = [];
          activeMathQuestionRef.current = question.name;

          const rect = wrapper.getBoundingClientRect();
          mathInputPositionRef.current = {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          };

          renderAstDisplay(question.name);
          katexDisplay.focus();
          setShowMathKeyboard(true);
        };

        katexDisplay.addEventListener("click", (e) => {
          e.stopPropagation();
        });

        katexDisplay.addEventListener("mousedown", (e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();

          if (!isActive()) {
            startEditing();
            requestAnimationFrame(() => {
              const newCursor = hitTestDeep(
                katexDisplay,
                astDocRef.current,
                e.clientX,
                e.clientY,
              );
              astCursorRef.current = newCursor;
              selectionAnchorRef.current = newCursor;
              renderAstDisplay(question.name);
            });
          } else {
            const newCursor = hitTestDeep(
              katexDisplay,
              astDocRef.current,
              e.clientX,
              e.clientY,
            );
            astCursorRef.current = newCursor;
            selectionAnchorRef.current = newCursor;
            renderAstDisplay(question.name);
          }

          const onMouseMove = (moveEvent) => {
            if (!isActive()) return;
            const moveCursor = hitTestDeep(
              katexDisplay,
              astDocRef.current,
              moveEvent.clientX,
              moveEvent.clientY,
            );
            astCursorRef.current = moveCursor;
            renderAstDisplay(question.name);
          };

          const onMouseUp = () => {
            if (selectionAnchorRef.current && astCursorRef.current) {
              const range = normalizeSelection(
                selectionAnchorRef.current,
                astCursorRef.current,
              );
              if (!range) {
                selectionAnchorRef.current = null;
                renderAstDisplay(question.name);
              }
            }
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
          };

          document.addEventListener("mousemove", onMouseMove);
          document.addEventListener("mouseup", onMouseUp);
        });

        wrapper.addEventListener("click", (e) => {
          if (e.target === wrapper) startEditing();
        });

        wrapper.append(katexDisplay);

        inputEl.style.display = "none";
        inputEl.parentNode?.insertBefore(wrapper, inputEl.nextSibling);
      }
    });

    model.onValueChanged.add((sender) => {
      if (submitStateRef.current !== "submitted")
        debouncedAutoSave(sender.data);
    });

    setSurveyModel(model);
  }, [surveyData, savedResponses]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleMathKeyPress = useCallback(
    (keyDef) => {
      const questionName = activeMathQuestionRef.current;
      if (!questionName) return;

      let doc = astDocRef.current;
      let cursor = astCursorRef.current;
      if (!doc || !cursor) return;

      // Actions that don't mutate the document — handle before pushUndo
      if (keyDef.type === "action") {
        if (keyDef.value === "CURSOR_LEFT" || keyDef.value === "CURSOR_RIGHT") {
          if (keyDef.value === "CURSOR_LEFT") cursor = moveLeft(doc, cursor);
          else cursor = moveRight(doc, cursor);
          selectionAnchorRef.current = null;
          astCursorRef.current = cursor;
          renderAstDisplay(questionName);
          return;
        }
        if (keyDef.value === "UNDO") {
          performUndo(questionName);
          return;
        }
        if (keyDef.value === "REDO") {
          performRedo(questionName);
          return;
        }
      }

      pushUndo();

      const selResult = deleteSelectionIfPresent(
        doc,
        cursor,
        selectionAnchorRef.current,
      );
      if (selResult) {
        doc = selResult.doc;
        cursor = selResult.cursor;
      }
      selectionAnchorRef.current = null;

      if (keyDef.type === "action") {
        switch (keyDef.value) {
          case "BACKSPACE": {
            const result = backspace(doc, cursor);
            doc = result.doc;
            cursor = result.cursor;
            break;
          }
          default:
            return;
        }
      } else {
        const insertion = mapKeyToInsertion(keyDef.value);
        if (insertion) {
          const result = applyInsertion(doc, cursor, insertion);
          doc = result.doc;
          cursor = result.cursor;
        }
      }

      astDocRef.current = doc;
      astCursorRef.current = cursor;
      mathInputValueRef.current = serializeToLatex(doc);
      renderAstDisplay(questionName);
    },
    [renderAstDisplay, pushUndo, performUndo, performRedo],
  );

  const handleMathKeyboardClose = useCallback(() => {
    const questionName = activeMathQuestionRef.current;
    const model = surveyModelRef.current;

    const finalValue = astDocRef.current
      ? serializeToLatex(astDocRef.current)
      : mathInputValueRef.current;

    if (model && questionName) {
      const q = model.getQuestionByName(questionName);
      if (q) {
        q.value = finalValue || undefined;
      }
    }

    if (questionName) {
      const katexDisplay = document.querySelector(
        `.et-katex-display[data-question-name="${questionName}"]`,
      );
      if (katexDisplay) {
        const cursorDom = katexDisplay.querySelector(".latex-cursor-dom");
        if (cursorDom) cursorDom.remove();
        katexDisplay.classList.remove("et-katex-editing");
        if (finalValue) {
          renderKatexWrapped(katexDisplay, finalValue);
          katexDisplay.style.color = "";
        } else {
          katexDisplay.textContent = "Type your answer here";
          katexDisplay.style.color = "#9ca3af";
        }
      }
    }

    activeMathQuestionRef.current = null;
    mathInputValueRef.current = "";
    mathInputPositionRef.current = null;
    astDocRef.current = null;
    astCursorRef.current = null;
    selectionAnchorRef.current = null;
    setMathInputDisplayValue("");
    setMathInputDisplayCursor(null);
    setShowMathKeyboard(false);
  }, []);

  useEffect(() => {
    if (!showMathKeyboard) return;

    const handleKeyDown = (e) => {
      const questionName = activeMathQuestionRef.current;
      if (!questionName) return;

      let doc = astDocRef.current;
      let cursor = astCursorRef.current;
      if (!doc || !cursor) return;

      const anchor = selectionAnchorRef.current;
      let result = null;
      let newAnchor = undefined;

      if (e.key === "Backspace") {
        e.preventDefault();
        pushUndo();
        const selResult = deleteSelectionIfPresent(doc, cursor, anchor);
        if (selResult) {
          result = selResult;
          newAnchor = null;
        } else {
          result = backspace(doc, cursor);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (e.shiftKey) {
          newAnchor = anchor ?? cursor;
          result = { doc, cursor: moveLeftClamped(doc, cursor) };
        } else if (anchor) {
          const range = normalizeSelection(anchor, cursor);
          newAnchor = null;
          result = range
            ? {
                doc,
                cursor: {
                  containerId: range.containerId,
                  containerField: range.containerField,
                  offset: range.startOffset,
                  cellIndex: range.cellIndex,
                },
              }
            : { doc, cursor: moveLeft(doc, cursor) };
        } else {
          result = { doc, cursor: moveLeft(doc, cursor) };
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (e.shiftKey) {
          newAnchor = anchor ?? cursor;
          result = { doc, cursor: moveRightClamped(doc, cursor) };
        } else if (anchor) {
          const range = normalizeSelection(anchor, cursor);
          newAnchor = null;
          result = range
            ? {
                doc,
                cursor: {
                  containerId: range.containerId,
                  containerField: range.containerField,
                  offset: range.endOffset,
                  cellIndex: range.cellIndex,
                },
              }
            : { doc, cursor: moveRight(doc, cursor) };
        } else {
          result = { doc, cursor: moveRight(doc, cursor) };
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (anchor && !e.shiftKey) newAnchor = null;
        if (!e.shiftKey) {
          const el = document.querySelector(
            `.et-katex-display[data-question-name="${questionName}"]`,
          );
          const visualOffset =
            el && cursor.containerId === "root"
              ? visualMoveUpDown(el, cursor, "up")
              : null;
          if (visualOffset !== null) {
            result = {
              doc,
              cursor: {
                containerId: "root",
                containerField: "nodes",
                offset: visualOffset,
              },
            };
          } else {
            result = { doc, cursor: moveUp(doc, cursor) };
          }
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (anchor && !e.shiftKey) newAnchor = null;
        if (!e.shiftKey) {
          const el = document.querySelector(
            `.et-katex-display[data-question-name="${questionName}"]`,
          );
          const visualOffset =
            el && cursor.containerId === "root"
              ? visualMoveUpDown(el, cursor, "down")
              : null;
          if (visualOffset !== null) {
            result = {
              doc,
              cursor: {
                containerId: "root",
                containerField: "nodes",
                offset: visualOffset,
              },
            };
          } else {
            result = { doc, cursor: moveDown(doc, cursor) };
          }
        }
      } else if (e.key === "Tab") {
        e.preventDefault();
        newAnchor = null;
        result = { doc, cursor: tabForward(doc, cursor) };
      } else if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        handleMathKeyboardClose();
        return;
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        performUndo(questionName);
        return;
      } else if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "y" || (e.key === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        performRedo(questionName);
        return;
      } else if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        const sel = selectAll(doc, cursor);
        newAnchor = sel.anchor;
        result = { doc, cursor: sel.focus };
      } else if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        e.preventDefault();
        if (anchor) {
          const range = normalizeSelection(anchor, cursor);
          if (range) {
            const selectedDoc = { nodes: getSelectedNodes(doc, range) };
            const latex = serializeToLatex(selectedDoc);
            if (latex) navigator.clipboard.writeText(latex);
          }
        } else {
          const latex = serializeToLatex(doc);
          if (latex) navigator.clipboard.writeText(latex);
        }
        return;
      } else if ((e.ctrlKey || e.metaKey) && e.key === "x") {
        e.preventDefault();
        if (anchor) {
          const range = normalizeSelection(anchor, cursor);
          if (range) {
            const selectedDoc = { nodes: getSelectedNodes(doc, range) };
            const latex = serializeToLatex(selectedDoc);
            if (latex) navigator.clipboard.writeText(latex);
            pushUndo();
            const delResult = deleteRange(doc, range);
            astDocRef.current = delResult.doc;
            astCursorRef.current = delResult.cursor;
            selectionAnchorRef.current = null;
            mathInputValueRef.current = serializeToLatex(delResult.doc);
            renderAstDisplay(questionName);
          }
        }
        return;
      } else if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        e.preventDefault();
        pushUndo();
        navigator.clipboard.readText().then((text) => {
          if (!text) return;
          let current = {
            doc: astDocRef.current,
            cursor: astCursorRef.current,
          };
          if (!current.doc || !current.cursor) return;

          const selResult = deleteSelectionIfPresent(
            current.doc,
            current.cursor,
            selectionAnchorRef.current,
          );
          if (selResult) current = selResult;

          // Parse clipboard text as LaTeX and splice nodes into the document
          const parsed = parseLatex(text);
          current = insertNodes(current.doc, current.cursor, parsed.nodes);

          astDocRef.current = current.doc;
          astCursorRef.current = current.cursor;
          selectionAnchorRef.current = null;
          mathInputValueRef.current = serializeToLatex(current.doc);
          renderAstDisplay(questionName);
        });
        return;
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        pushUndo();
        let currentDoc = doc;
        let currentCursor = cursor;

        const selResult = deleteSelectionIfPresent(doc, cursor, anchor);
        if (selResult) {
          currentDoc = selResult.doc;
          currentCursor = selResult.cursor;
        }

        const insertion = mapKeyToInsertion(e.key);
        if (insertion) {
          result = applyInsertion(currentDoc, currentCursor, insertion);
        }
        newAnchor = null;
      }

      if (result) {
        astDocRef.current = result.doc;
        astCursorRef.current = result.cursor;
        mathInputValueRef.current = serializeToLatex(result.doc);
      }
      if (newAnchor !== undefined) {
        selectionAnchorRef.current = newAnchor;
      }
      if (result || newAnchor !== undefined) {
        renderAstDisplay(questionName);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    showMathKeyboard,
    handleMathKeyboardClose,
    renderAstDisplay,
    pushUndo,
    performUndo,
    performRedo,
  ]);

  const handleSubmit = async () => {
    if (!surveyModel || submitState !== "idle") return;

    // Flush any active math input so answers typed without closing the keyboard are included.
    handleMathKeyboardClose();

    // A warning left over from a failed submit would sit next to the countdown if this one succeeds.
    const notifications = useNotificationStore.getState();
    notifications.notifications
      .filter((n) => n.message === SUBMIT_FAILED_MESSAGE)
      .forEach((n) => notifications.removeNotification(n.id));

    setSubmitState("submitting");
    let result;
    try {
      const data = surveyModel.data;
      useAssessmentStore
        .getState()
        .setPercentAnswered(type, computePercentAnswered(surveyModel));
      result = await submitAttempt(ticketId, data);
    } catch {
      setSubmitState("idle");
      // A 503 (grading down) leaves the attempt open, so resubmitting is safe.
      notifications.showNotification(
        SUBMIT_FAILED_MESSAGE,
        NotificationType.WARNING,
        5000,
        NOTIFICATION_STACKS[NotificationStackId.OVERLAY],
      );
      return;
    }
    setSubmitState("submitted");

    if (config.autoTransition) {
      const { title, durationMs } = config.autoTransition;
      const completed = await showDynamicTutorViewNotification(
        title,
        { seconds: Math.ceil(durationMs / 1000) },
        "",
        NotificationType.NEXT_BITE,
        durationMs,
        NOTIFICATION_STACKS[NotificationStackId.OVERLAY],
      );
      if (completed && onComplete) onComplete(result);
    } else {
      if (onComplete) onComplete(result);
    }
  };

  const handleSaveAndExit = async () => {
    if (submitState !== "submitted") await flushAutoSave();
    if (onSaveAndExit) onSaveAndExit();
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none"
        aria-hidden="true"
      />
      <div
        className={`relative z-10 bg-white flex flex-col overflow-hidden ${isMobile || isMobileLandscape ? "" : "rounded-[20px] shadow-xl"}`}
        style={
          isMobile || isMobileLandscape
            ? { width: "100vw", height: "100dvh" }
            : { width: "90vw", height: "90vh" }
        }
      >
        {!surveyModel ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3">
            <Loader2
              className="w-8 h-8 animate-spin"
              style={{ color: config.survey.accentColor }}
            />
            <span
              className="text-sm text-gray-500 font-medium"
              style={{ fontFamily: "Mulish, sans-serif" }}
            >
              Loading {config.label.toLowerCase()}…
            </span>
          </div>
        ) : (
          <>
            <div
              className={`sticky top-0 z-10 bg-white border-b border-gray-100 flex items-center justify-end gap-3 ${isMobile || isMobileLandscape ? "px-4 py-2 min-h-[56px]" : "px-6 py-3 min-h-[70px]"}`}
            >
              {submitState === "idle" && (
                <button
                  onClick={handleSubmit}
                  className="text-white transition-colors"
                  style={{
                    backgroundColor: config.survey.accentColor,
                    height: isMobile || isMobileLandscape ? 38 : 45,
                    borderRadius: 10,
                    padding:
                      isMobile || isMobileLandscape ? "6px 16px" : "8px 20px",
                    whiteSpace: "nowrap",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile || isMobileLandscape ? 13 : 15,
                    lineHeight: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      config.survey.accentHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      config.survey.accentColor;
                  }}
                >
                  {config.survey.submitLabel}
                </button>
              )}
              {submitState === "submitting" && (
                <div
                  className="flex items-center justify-center gap-2 bg-white border border-[#EBEBEB]"
                  style={{
                    width: isMobile || isMobileLandscape ? 112 : 128,
                    height: isMobile || isMobileLandscape ? 38 : 45,
                    borderRadius: 10,
                    padding: "6px 10px",
                  }}
                >
                  <Loader2
                    className="w-3.5 h-3.5 animate-spin"
                    style={{ color: config.survey.accentColor }}
                  />
                  <span
                    style={{
                      fontFamily: "Mulish, sans-serif",
                      fontWeight: 700,
                      fontSize: isMobile || isMobileLandscape ? 13 : 15,
                      lineHeight: "100%",
                      color: config.survey.accentColor,
                    }}
                  >
                    Submitting
                  </span>
                </div>
              )}
              {submitState === "submitted" && (
                <>
                  <div className="flex items-center gap-2">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.9784 6.14424C13.2531 6.4189 13.2531 6.86412 12.9784 7.13864L8.26144 11.8558C7.98679 12.1303 7.5417 12.1303 7.26704 11.8558L5.02158 9.61015C4.74692 9.33563 4.74692 8.89041 5.02158 8.61589C5.2961 8.34123 5.74132 8.34123 6.01584 8.61589L7.76418 10.3642L11.984 6.14424C12.2587 5.86972 12.7039 5.86972 12.9784 6.14424ZM18 9C18 13.9747 13.9741 18 9 18C4.02525 18 0 13.9741 0 9C0 4.02525 4.02594 0 9 0C13.9747 0 18 4.02594 18 9ZM16.5938 9C16.5938 4.80254 13.1969 1.40625 9 1.40625C4.80254 1.40625 1.40625 4.80309 1.40625 9C1.40625 13.1975 4.80309 16.5938 9 16.5938C13.1975 16.5938 16.5938 13.1969 16.5938 9Z"
                        fill={config.survey.accentColor}
                      />
                    </svg>
                    <span
                      style={{
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile || isMobileLandscape ? 13 : 15,
                        lineHeight: "100%",
                        color: config.survey.accentColor,
                      }}
                    >
                      Successfully Submitted
                    </span>
                  </div>
                  {!config.autoTransition && (
                    <button
                      onClick={handleSaveAndExit}
                      className="text-white transition-colors"
                      style={{
                        backgroundColor: config.survey.accentColor,
                        height: isMobile || isMobileLandscape ? 38 : 45,
                        borderRadius: 10,
                        padding:
                          isMobile || isMobileLandscape
                            ? "6px 16px"
                            : "8px 20px",
                        whiteSpace: "nowrap",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile || isMobileLandscape ? 13 : 15,
                        lineHeight: "100%",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          config.survey.accentHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          config.survey.accentColor;
                      }}
                    >
                      Continue
                    </button>
                  )}
                </>
              )}
            </div>

            <div
              className="et-preview-mode flex-1 overflow-y-auto bg-white"
              style={{
                padding:
                  isMobile || isMobileLandscape ? "0 12px 16px" : "0 24px 24px",
              }}
            >
              <Survey model={surveyModel} />
            </div>
          </>
        )}
      </div>

      {/* No currentValue: the question's own box already shows the typed math */}
      {showMathKeyboard &&
        createPortal(
          // The wrapper exists only to carry translate="no": a portal renders
          // into document.body, so it inherits nothing from this React tree,
          // and LatexKeyboard takes no className of its own. Deliberately
          // unstyled — the keyboard root is position: fixed, which a bare div
          // leaves alone, whereas a positioned wrapper would move it.
          <div {...noTranslateProps()}>
            <LatexKeyboard
              inputPosition={mathInputPositionRef.current}
              onKeyPress={handleMathKeyPress}
              onClose={handleMathKeyboardClose}
              renderMath={renderMath}
            />
          </div>,
          document.body,
        )}
      <div
        className="fixed z-[10000]"
        style={{ bottom: "calc(5vh)", right: "calc(5vw)" }}
      >
        <NotificationStack
          stack={NOTIFICATION_STACKS[NotificationStackId.OVERLAY]}
        />
      </div>

      <ImageLightbox
        isOpen={!!lightboxImage}
        src={lightboxImage?.src ?? null}
        alt={lightboxImage?.alt ?? ""}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

export default AssessmentSurvey;
